/**
 * Line Item Display Helpers
 * Provides consistent display logic for line items across the application
 * Handles manual overrides for descriptions and pricing
 */

interface LineItemWithDescription {
  description: string;
  manual_description?: string | null;
  original_description?: string | null;
}

interface LineItemWithCost {
  total_cost_snapshot: number;
  manual_override: boolean;
  manual_total_cost?: number | null;
}

interface LineItemWithOverrides extends LineItemWithDescription, LineItemWithCost {}

/**
 * Get the display description for a line item
 * Manual description takes precedence over regular description
 */
export const getDisplayDescription = (lineItem: LineItemWithDescription): string => {
  return lineItem.manual_description || lineItem.description;
};

/**
 * Get the display total cost for a line item
 * Manual override takes precedence over snapshot cost
 */
export const getDisplayTotalCost = (lineItem: LineItemWithCost): number => {
  return lineItem.manual_override && lineItem.manual_total_cost !== null
    ? lineItem.manual_total_cost
    : lineItem.total_cost_snapshot;
};

/**
 * Check if a line item has any manual overrides (description or pricing)
 */
export const hasManualOverrides = (lineItem: LineItemWithOverrides): boolean => {
  return lineItem.manual_override || !!lineItem.manual_description;
};

/**
 * Validate description input
 * Returns validation result with errors and warnings
 */
export const validateDescription = (description: string): {
  valid: boolean;
  error?: string;
  warning?: string;
} => {
  const trimmed = description.trim();
  
  // Empty check
  if (!trimmed) {
    return { valid: false, error: 'Description cannot be empty' };
  }
  
  // Minimum length (hard block)
  if (trimmed.length < 3) {
    return { valid: false, error: 'Description must be at least 3 characters' };
  }
  
  // Short description warning
  if (trimmed.length < 5) {
    return { 
      valid: true, 
      warning: 'Short description - consider adding more detail for clarity' 
    };
  }
  
  // Long description warning
  if (trimmed.length > 500) {
    return { 
      valid: true, 
      warning: 'Long description may be truncated in some views' 
    };
  }
  
  // HTML tag check (XSS prevention)
  if (/<[^>]*>/g.test(trimmed)) {
    return { valid: false, error: 'HTML tags are not allowed in descriptions' };
  }
  
  return { valid: true };
};

/**
 * Get the appropriate badge variant for component type
 * Used for visual indicators in the UI
 */
export const getComponentTypeBadge = (componentType: string): {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
  className?: string;
} => {
  switch (componentType) {
    case 'internal_line_item':
      return { label: 'Internal', variant: 'default' };
    case 'supplier_line_item':
      return { label: 'Supplier', variant: 'secondary' };
    case 'assembly':
      return { label: 'Assembly', variant: 'outline' };
    case 'custom':
      return { label: 'Custom', variant: 'secondary', className: 'bg-orange-100 text-orange-800' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
};

/**
 * Calculate unit final cost from margin percentage
 * Formula: unitFinal = unitBase / (1 - margin/100)
 */
export const calculateUnitFinalFromMargin = (
  unitBaseCost: number, 
  marginPercent: number
): number => {
  const clampedMargin = Math.max(0, marginPercent);
  if (clampedMargin >= 99.9) return unitBaseCost * 100; // Cap at 10,000% markup
  if (clampedMargin <= 0) return unitBaseCost;
  return unitBaseCost / (1 - clampedMargin / 100);
};

/**
 * Calculate margin percentage from unit costs
 * Formula: margin = ((unitFinal - unitBase) / unitFinal) × 100
 */
export const calculateMarginFromUnitFinal = (
  unitBaseCost: number,
  unitFinalCost: number
): number => {
  if (unitFinalCost <= 0) return 0;
  return Math.max(0, ((unitFinalCost - unitBaseCost) / unitFinalCost) * 100);
};
